import React, { FC, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import clsx from 'clsx';
import { MSlot } from '../../molecules/MSlot/index';
import { MMediaSelector } from '../../molecules/MMediaSelector/index';
import { MButtonOverlay } from '../../molecules/MButtonOverlay/index';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import * as Styled from './OCarouselGalleryHero.styles';
import {
  OCarouselGalleryHeroProps,
  OCarouselGalleryHeroImageScale,
  OCarouselGalleryHeroMediaType,
  OCarouselGalleryHeroCarouselType,
} from './OCarouselGalleryHero.types';
import { MCarouselControls } from '../../molecules/MCarouselControls';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';

const getCarouselControlsType = (type?: OCarouselGalleryHeroCarouselType): MCarouselControlsType =>
  type === OCarouselGalleryHeroCarouselType.Numbers ? MCarouselControlsType.Numbers : MCarouselControlsType.Dots;

export const OCarouselGalleryHero: FC<OCarouselGalleryHeroProps> = ({
  imageScale = OCarouselGalleryHeroImageScale.Medium,
  mediaType: _mediaType = OCarouselGalleryHeroMediaType.Image,
  carouselType = OCarouselGalleryHeroCarouselType.Dots,
  gallery = false,
  virtualTour = false,
  isAuthorMode = false,
  mMediaSelectorMulti = [],
  mButtonOverlayGallery,
  mButtonOverlayVirtualTour,
  trackingProperties: _trackingProperties,
  cqPath,
  className,
  ...nativeProps
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(mMediaSelectorMulti?.length || 0);

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentSlide(swiper.activeIndex);
  };

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  const handlePrev = () => {
    swiperRef.current?.slidePrev();
  };

  const getHeightClass = () => {
    switch (imageScale) {
      case OCarouselGalleryHeroImageScale.Small:
        return 'scale-small';
      case OCarouselGalleryHeroImageScale.Large:
        return 'scale-large';
      case OCarouselGalleryHeroImageScale.Medium:
      default:
        return 'scale-medium';
    }
  };

  return (
    <Styled.Wrapper
      {...nativeProps}
      className={clsx('o-carousel-gallery-hero', className, getHeightClass())}
      data-testid="mi-web-ui-kit-OCarouselGalleryHero"
    >
      <Styled.CarouselContainer>
        <Swiper
          modules={[Navigation, Pagination, A11y]}
          spaceBetween={0}
          slidesPerView={1}
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper;
            setTotalSlides(swiper.slides.length);
          }}
          onSlideChange={handleSlideChange}
          loop={false}
          className="carousel-swiper"
        >
          {isAuthorMode && cqPath ? (
            <SwiperSlide>
              <MSlot pagePath={cqPath} itemPath={`${cqPath}/mMediaSelectorMulti`} />
            </SwiperSlide>
          ) : (
            mMediaSelectorMulti?.map((mediaItem, index) => (
              <SwiperSlide key={index}>
                <Styled.SlideContent>
                  <MMediaSelector {...mediaItem} />
                </Styled.SlideContent>
              </SwiperSlide>
            ))
          )}
        </Swiper>

        <Styled.ControlsContainer>
          <MCarouselControls
            type={getCarouselControlsType(carouselType)}
            totalSlides={totalSlides}
            currentSlide={currentSlide}
            onNext={handleNext}
            onPrev={handlePrev}
            imageOverlay={true}
          />

          {(gallery || virtualTour) && (
            <Styled.ButtonGroup>
              {gallery && (
                <MButtonOverlay
                  text={mButtonOverlayGallery?.text || 'Gallery'}
                  imageOverlay={true}
                  iconOnly={mButtonOverlayGallery?.iconOnly ?? false}
                  aIcon={mButtonOverlayGallery?.aIcon || { icon: 'icon-photo-gallery', size: AIconSize.M }}
                  {...mButtonOverlayGallery}
                />
              )}
              {virtualTour && (
                <MButtonOverlay
                  text={mButtonOverlayVirtualTour?.text || 'Virtual Tour'}
                  imageOverlay={true}
                  iconOnly={mButtonOverlayVirtualTour?.iconOnly ?? false}
                  aIcon={mButtonOverlayVirtualTour?.aIcon || { icon: 'icon-360-image', size: AIconSize.M }}
                  {...mButtonOverlayVirtualTour}
                />
              )}
            </Styled.ButtonGroup>
          )}
        </Styled.ControlsContainer>
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};

export * from './OCarouselGalleryHero.types';
